import React from "react";
import { Car, LogOut, Menu, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getInstitutionInitials } from "../../utils/institutionBranding";
import TransportWorkspace, { transportSections } from "./TransportWorkspace";

const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const roleLabel = (value) =>
  String(value || "staff")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const TransportPortal = () => {
  const { user, profile, signOut } = useAuth();
  const { institutionSettings: institution } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const roles = [
    profile?.primary_role,
    ...(Array.isArray(profile?.secondary_roles) ? profile.secondary_roles : []),
  ]
    .map(normalizeRole)
    .filter(Boolean);
  const manager = roles.some((role) =>
    [
      "transport_officer",
      "system_administrator",
      "deputy_principal_administration",
    ].includes(role),
  );
  const requestedView = searchParams.get("view") || "overview";
  const view = manager
    ? transportSections.some((section) => section.id === requestedView)
      ? requestedView
      : "overview"
    : "requests";
  const staffName =
    profile?.full_name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "Staff member";
  const initials = getInstitutionInitials(institution);
  const notify = React.useCallback((message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 4000);
  }, []);
  const logout = async () => {
    await signOut();
    navigate("/login");
  };
  const setView = (nextView) => {
    navigate(
      nextView === "overview" ? "/transport" : `/transport?view=${nextView}`,
    );
    setMobileOpen(false);
  };
  const visibleSections = manager
    ? transportSections
    : transportSections.filter((section) => section.id === "requests");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-[100] bg-primary text-primary-foreground shadow-lg">
        <div className="mx-auto flex min-h-16 max-w-[1600px] items-center gap-4 px-5">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex shrink-0 items-center gap-3"
            aria-label="Portal home"
          >
            {institution?.logo_url ? (
              <img
                src={institution.logo_url}
                alt="Institution logo"
                className="h-10 w-10 rounded bg-white object-contain p-1"
              />
            ) : (
              <span className="grid h-10 w-10 place-items-center rounded bg-white/15">
                <Car className="h-5 w-5" />
              </span>
            )}
            <span className="font-bold tracking-[0.18em]">{initials}</span>
          </button>
          <div className="hidden min-w-0 lg:block">
            <p className="truncate text-sm font-bold">Transport operations</p>
            <p className="truncate text-xs text-primary-foreground/70">
              Fleet, dispatch and staff requests
            </p>
          </div>
          <nav className="ml-auto hidden min-w-0 flex-1 items-center justify-center gap-1 overflow-x-auto lg:flex">
            {visibleSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setView(section.id)}
                  className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${view === section.id ? "bg-white text-primary" : "hover:bg-white/10"}`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
          </nav>
          <button
            type="button"
            className="ml-auto lg:hidden"
            onClick={() => setMobileOpen((value) => !value)}
            aria-label="Toggle transport navigation"
          >
            {mobileOpen ? <X /> : <Menu />}
          </button>
          <div className="hidden items-center gap-3 lg:flex">
            <div className="text-right">
              <p className="max-w-44 truncate text-sm font-semibold">
                {staffName}
              </p>
              <p className="text-xs text-primary-foreground/70">
                {roleLabel(profile?.primary_role)}
              </p>
            </div>
            <button
              type="button"
              onClick={logout}
              title="Sign out"
              className="rounded-lg p-2 hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
        {mobileOpen && (
          <nav className="border-t border-white/10 bg-white px-5 py-2 text-slate-800 lg:hidden">
            {visibleSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setView(section.id)}
                  className={`flex w-full items-center gap-2 rounded px-3 py-3 text-left text-sm font-semibold ${view === section.id ? "bg-blue-50 text-blue-700" : "hover:bg-blue-50 hover:text-blue-700"}`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center gap-2 border-t py-3 text-left text-sm text-rose-700"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </nav>
        )}
      </header>
      <main className="mx-auto max-w-[1600px] space-y-6 px-5 py-7">
        <section className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-blue-700">
              {manager ? "Transport operations" : "Staff services"}
            </p>
            <h1 className="mt-1 text-3xl font-bold text-slate-950">
              {manager
                ? "Fleet command centre"
                : "Request institutional transport"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {manager
                ? "Monitor vehicles, dispatch trips, manage risk and keep the institution moving."
                : "Send a route request to the Transport Office and track its approval."}
            </p>
          </div>
          {manager && (
            <button
              type="button"
              onClick={() => setView("requests")}
              className="rounded-lg border bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              View request queue
            </button>
          )}
        </section>
        <TransportWorkspace
          notify={notify}
          view={view}
          manager={manager}
          onViewChange={setView}
        />
      </main>
      {toast && (
        <div
          className={`fixed bottom-5 right-5 z-[200] max-w-md rounded-lg border px-4 py-3 text-sm font-medium shadow-xl ${toast.type === "error" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default TransportPortal;
